import React, { useState } from "react";
import api from "../lib/api";
import { LOGO_WIDE } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, Mail, Loader2, LockKeyhole, Activity } from "lucide-react";
import { toast } from "sonner";

const Trust = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 text-white/80 text-sm">
    <Icon size={14} style={{ color: "#00C7FF" }}/>
    {label}
  </div>
);

export default function Login() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [devLink, setDevLink] = useState(null);
  const [smtpFailed, setSmtpFailed] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/magic-link/start", { email });
      setSent(true);
      if (data.dev_link) setDevLink(data.dev_link);
      setSmtpFailed(!!data.smtp_failed);
      if (data.smtp_failed) {
        toast.error("Email delivery failed — use the sign-in link below.");
      } else {
        toast.success("If your email is authorized, a sign-in link is on the way.");
      }
    } catch (err) { toast.error("Something went wrong"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen relative flex flex-col overflow-hidden" style={{ background: "#0F1644" }}>
      {/* Background: subtle datacenter texture + radial glow */}
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="https://images.pexels.com/photos/17323801/pexels-photo-17323801.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.12]"
        />
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 75% 35%, rgba(0,199,255,0.18) 0%, transparent 55%), linear-gradient(180deg, rgba(15,22,68,0.85) 0%, rgba(15,22,68,1) 90%)" }}/>
      </div>

      {/* Main content */}
      <main className="relative flex-1 grid grid-cols-1 lg:grid-cols-12 max-w-7xl mx-auto w-full px-6 py-12 gap-12 items-center">
        {/* LEFT — brand narrative */}
        <section className="lg:col-span-7 max-w-xl">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] mb-8 px-3 py-1.5 rounded-full" style={{ color: "#00C7FF", background: "rgba(0,199,255,0.10)", border: "1px solid rgba(0,199,255,0.25)" }}>
            <ShieldCheck size={14}/> Secure Client Onboarding
          </div>
          <h1 className="font-bold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Enterprise IT.<br/>
            Speed. Strength.<br/>
            <span style={{ color: "#00C7FF" }}>Security.</span>
          </h1>
          <p className="mt-8 text-white/75 text-lg leading-relaxed max-w-md">
            Your secure portal to onboard with Sentry Cloud IT. Passwordless sign-in, encrypted in transit, audited end-to-end.
          </p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-md">
            <Trust icon={LockKeyhole} label="Single-use links" />
            <Trust icon={Mail} label="No passwords" />
            <Trust icon={Activity} label="Full audit log" />
          </div>
        </section>

        {/* RIGHT — Sign-in card */}
        <section className="lg:col-span-5 w-full max-w-md mx-auto lg:mx-0">
          <div className="relative rounded-2xl bg-white shadow-2xl overflow-hidden" style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}>
            {/* Full-width dark navy banner with bigger logo */}
            <div className="w-full flex justify-center py-7" style={{ background: "#0F1644" }}>
              <img src={LOGO_WIDE} alt="Sentry Cloud IT" style={{ height: 76, width: "auto" }} data-testid="login-logo"/>
            </div>

            <div className="px-8 pt-7 pb-10">
              <h2 className="text-2xl font-bold tracking-tight text-brand-primary text-center">Sign in</h2>
              <p className="text-sm text-slate-500 mt-1.5 text-center">We'll email you a one-click sign-in link.</p>

              {!sent ? (
                <form onSubmit={submit} className="mt-7 space-y-5">
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-slate-800 text-sm">Work email</Label>
                    <Input
                      id="email" type="email" value={email}
                      onChange={(e) => setEmail(e.target.value)} required
                      placeholder="you@company.com"
                      className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-400 h-12 focus-visible:ring-2 focus-visible:ring-[#00C7FF] focus-visible:border-[#00C7FF] focus-visible:ring-offset-0"
                      data-testid="login-email-input"
                    />
                  </div>
                  <Button
                    type="submit" disabled={loading}
                    className="w-full font-semibold h-12 text-base hover:opacity-95 transition"
                    style={{ background: "#0F1644", color: "#fff" }}
                    data-testid="login-submit-btn"
                  >
                    {loading ? <Loader2 className="animate-spin" size={16}/> : <><Mail size={16} className="mr-2"/>Send sign-in link</>}
                  </Button>
                </form>
              ) : (
                <div className="mt-7 space-y-4" data-testid="login-sent-message">
                  {smtpFailed ? (
                    <div className="rounded-lg p-4 text-sm bg-amber-50 border border-amber-300 text-amber-900" data-testid="smtp-failed-warning">
                      <div className="font-semibold">Email delivery failed.</div>
                      <div className="mt-1">Our mail server rejected the SMTP credentials, so no email was sent. Click the sign-in link below to continue — then update your SMTP settings once signed in.</div>
                    </div>
                  ) : (
                    <div className="rounded-lg p-4 text-sm bg-emerald-50 border border-emerald-200 text-emerald-800">
                      <div className="font-semibold">Check your inbox.</div>
                      <div className="mt-1">If <span className="font-mono text-emerald-900">{email}</span> is authorized, a sign-in link is on the way. It expires in 15 minutes.</div>
                    </div>
                  )}
                  {devLink && (
                    <div className={`rounded-lg p-4 text-xs ${smtpFailed ? "bg-amber-50 border border-amber-300" : "bg-slate-50 border border-slate-200"}`}>
                      <div className={`mb-1 ${smtpFailed ? "text-amber-900 font-semibold" : "text-slate-600"}`}>
                        {smtpFailed ? "Sign-in link (expires in 15 min):" : "Dev mode link:"}
                      </div>
                      <a href={devLink} className="break-all underline text-brand-primary" data-testid="dev-magic-link">{devLink}</a>
                    </div>
                  )}
                  <button onClick={() => { setSent(false); setEmail(""); setSmtpFailed(false); setDevLink(null); }} className="text-xs text-slate-500 hover:text-slate-700 underline w-full text-center" data-testid="login-try-another">Try another email</button>
                </div>
              )}

              <div className="mt-7 pt-5 border-t border-slate-200 text-xs text-slate-500 text-center">
                Authorized emails only · Single-use · 15 min · Encrypted
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer — same Presidential Blue background, flows naturally */}
      <footer className="relative border-t border-white/10" data-testid="brand-footer">
        <div className="max-w-7xl mx-auto px-6 py-7 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-5">
            <img src={LOGO_WIDE} alt="Sentry Cloud IT" style={{ height: 44, width: "auto" }}/>
            <div className="hidden md:block">
              <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "#00C7FF" }}>Speed · Strength · Security</div>
            </div>
          </div>
          <div className="text-xs text-white/60 text-center md:text-right">
            <div>© {new Date().getFullYear()} Sentry Cloud IT. All rights reserved.</div>
            <a href="https://sentrycloudit.com" target="_blank" rel="noreferrer" className="hover:underline" style={{ color: "#00C7FF" }}>sentrycloudit.com</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
